import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Upload, X, FileText, Image, Loader2, Trash2 } from 'lucide-react';
import { uploadMaterialFile, deleteFile, getFileUrl } from '@/lib/api';

const TYPE_LABELS = {
  photo: 'Foto',
  spec_sheet: 'Spezifikationsblatt',
  data_sheet: 'Datenblatt',
  certificate: 'Zertifikat',
  safety_data_sheet: 'Sicherheitsdatenblatt',
  other: 'Sonstiges',
};

export const MaterialFileUpload = ({ materialId, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [attachmentType, setAttachmentType] = useState('photo');
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    setProgress(0);

    try {
      for (const file of files) {
        await uploadMaterialFile(
          file,
          materialId,
          attachmentType,
          (e) => {
            if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
          }
        );
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
      onUploadComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const accept = attachmentType === 'photo'
    ? 'image/jpeg,image/png,image/gif,image/webp'
    : '.pdf,image/jpeg,image/png';

  return (
    <div className="space-y-3" data-testid="material-file-upload">
      <div className="flex items-center gap-2">
        <Select value={attachmentType} onValueChange={setAttachmentType}>
          <SelectTrigger className="w-[200px]" data-testid="upload-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-file-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Datei hochladen
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple
          onChange={handleUpload}
        />
      </div>
      {uploading && <Progress value={progress} className="h-1.5" />}
    </div>
  );
};

export const AttachmentsList = ({ attachments, onDelete, canManage }) => {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (att) => {
    setDeleting(att.attachment_id);
    try {
      await deleteFile(att.attachment_id);
      toast.success('Datei gelöscht');
      onDelete?.();
    } catch (err) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeleting(null);
    }
  };

  if (!attachments?.length) {
    return <p className="text-muted-foreground text-sm py-4">Keine Dokumente vorhanden</p>;
  }

  return (
    <div className="space-y-2" data-testid="attachments-list">
      {attachments.map((att) => {
        const isImage = att.type === 'photo';
        const fileUrl = att.file_url?.startsWith('/api/')
          ? `${process.env.REACT_APP_BACKEND_URL}${att.file_url}`
          : att.file_url;

        return (
          <div key={att.attachment_id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid={`attachment-${att.attachment_id}`}>
            {isImage ? <Image className="h-5 w-5 text-green-500" /> : <FileText className="h-5 w-5 text-orange-500" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm capitalize truncate">
                {TYPE_LABELS[att.type] || att.type?.replace(/_/g, ' ')}
              </p>
            </div>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-500 hover:underline"
            >
              Anzeigen
            </a>
            {canManage && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500"
                disabled={deleting === att.attachment_id}
                onClick={() => handleDelete(att)}
                data-testid={`delete-attachment-${att.attachment_id}`}
              >
                {deleting === att.attachment_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};
